import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Cake, Check, Loader2, MessageSquare, MessageSquareOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function whenLabel(days) {
  if (days === 0) return "today";
  if (days > 0) return `in ${days} day${days === 1 ? "" : "s"}`;
  return `${-days} day${days === -1 ? "" : "s"} ago`;
}

export default function BirthdayTreatsPanel({ authHeaders, toast }) {
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/staff/birthday-treats`, { headers: authHeaders });
      setItems(data.items || []);
    } catch { /* panel is non-critical */ }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const redeem = async (row) => {
    setBusyId(row.user_id);
    try {
      await axios.post(`${API}/staff/birthday-treats/${row.user_id}/redeem`, {}, { headers: authHeaders });
      toast({ title: "Dessert claimed", description: `${row.name}'s birthday treat is marked as used for this year.` });
      load();
    } catch (e) {
      toast({ title: "Couldn't mark claimed", description: e?.response?.data?.detail || e.message });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="mt-8 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)] p-5" data-testid="birthday-treats-panel">
      <div className="section-label mb-4 flex items-center gap-2">
        <Cake size={14} /> Birthday treats this week
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-[var(--mm-cream-muted)]">No guest birthdays in the next 7 days.</p>
      ) : (
        <div className="space-y-3">
          {items.map((row) => (
            <div
              key={row.user_id}
              className="flex flex-wrap items-center gap-3 border-b border-[var(--mm-line)]/60 pb-3 last:border-0 last:pb-0"
              data-testid={`birthday-treat-row-${row.user_id}`}
            >
              <div className="flex-1 min-w-[180px]">
                <div className="text-sm text-[var(--mm-cream)]">
                  {row.name}
                  {row.phone_hint && <span className="text-[var(--mm-cream-muted)]"> · {row.phone_hint}</span>}
                </div>
                <div className="text-xs text-[var(--mm-cream-muted)]">
                  Birthday {row.birthday_display} ({whenLabel(row.days_until)})
                </div>
              </div>
              <span
                className={`inline-flex items-center gap-1.5 text-[0.62rem] tracking-[0.18em] uppercase px-2 py-1 border ${
                  row.texted
                    ? "border-[var(--mm-accent)]/60 text-[var(--mm-accent)]"
                    : "border-[var(--mm-line)] text-[var(--mm-cream-muted)]"
                }`}
              >
                {row.texted ? <MessageSquare size={11} /> : <MessageSquareOff size={11} />}
                {row.texted ? "Texted" : "Not texted"}
              </span>
              {row.redeemed ? (
                <span className="inline-flex items-center gap-1.5 text-[0.62rem] tracking-[0.18em] uppercase px-3 py-2 border border-green-600/60 text-green-500">
                  <Check size={11} /> Claimed
                </span>
              ) : (
                <button
                  onClick={() => redeem(row)}
                  disabled={busyId === row.user_id}
                  data-testid={`redeem-treat-${row.user_id}`}
                  className="btn-primary inline-flex items-center gap-2 !px-4 !py-2 text-[0.62rem] disabled:opacity-50"
                >
                  {busyId === row.user_id ? <Loader2 size={12} className="animate-spin" /> : <Cake size={12} />}
                  Mark claimed
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
